/**
 * Campus banner signup form.
 *
 * The form markup/styling lives entirely in sections/campus-banner.liquid so
 * the design is fully custom. On submit, this posts the profile straight to
 * Klaviyo's public "Client Subscriptions" API (the same unauthenticated,
 * company_id-based endpoint Klaviyo's own onsite embeds use), so no Klaviyo
 * embed code or styling is ever loaded on the page.
 *
 * Docs: https://developers.klaviyo.com/en/reference/create_client_subscription
 */
(function () {
  var KLAVIYO_REVISION = '2024-10-15';
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';

  function digitsOnly(value) {
    return (value || '').replace(/[^\d+]/g, '');
  }

  // Klaviyo requires E.164 (+country code + national number). Local numbers
  // in many countries (including ZA) are written with a leading trunk 0
  // (e.g. 082 123 4567) — that 0 must be dropped before prefixing +27.
  function toE164(rawValue, dialCode) {
    var cleaned = digitsOnly(rawValue);
    if (!cleaned) return '';
    if (cleaned.charAt(0) === '+') return cleaned;

    var codeDigits = digitsOnly(dialCode || '').replace(/\D/g, '');
    if (!codeDigits) return '+' + cleaned;

    // Remove all leading trunk zeros (082..., 066..., 0066..., etc.)
    var national = cleaned.replace(/^0+/, '');
    if (!national) return '';

    // If the country code was typed without "+", don't prefix it twice
    // (e.g. +27 selected and user enters "27 82 123 4567").
    if (national.indexOf(codeDigits) === 0 && national.length > codeDigits.length) {
      national = national.slice(codeDigits.length);
    }

    return '+' + codeDigits + national;
  }

  function getSelectedPhoneCountry(form, root) {
    var phoneField = form.querySelector('[data-campus-banner-phone]');
    var countrySelect = phoneField
      ? phoneField.querySelector('[name="phone_country"]')
      : null;
    var selectedOption =
      countrySelect && countrySelect.options[countrySelect.selectedIndex];

    return {
      dialCode: countrySelect
        ? countrySelect.value
        : root.dataset.klaviyoPhoneCountryCode || '+1',
      countryIso: selectedOption ? selectedOption.dataset.countryIso || '' : '',
      countryName: selectedOption ? selectedOption.dataset.countryName || '' : ''
    };
  }

  function getPhoneE164(form, root) {
    var phoneField = form.querySelector('[data-campus-banner-phone]');
    var phoneInput = phoneField
      ? phoneField.querySelector('[name="phone"]')
      : form.querySelector('[name="phone"]');
    if (!phoneInput) return '';

    var national = (phoneInput.value || '').trim();
    if (!national) return '';

    var country = getSelectedPhoneCountry(form, root);
    return toE164(national, country.dialCode);
  }

  function resetPhoneFields(form, root) {
    var defaultCode = root.dataset.klaviyoPhoneCountryCode || '+1';
    form.querySelectorAll('[data-campus-banner-phone]').forEach(function (phoneField) {
      var countrySelect = phoneField.querySelector('[name="phone_country"]');
      var phoneInput = phoneField.querySelector('[name="phone"]');
      if (phoneInput) phoneInput.value = '';
      if (!countrySelect) return;

      var preferred = countrySelect.dataset.defaultCountry || defaultCode;
      var matched = false;
      Array.prototype.forEach.call(countrySelect.options, function (option) {
        if (!matched && option.value === preferred) {
          option.selected = true;
          matched = true;
        } else {
          option.selected = false;
        }
      });
      if (!matched && countrySelect.options.length) {
        countrySelect.options[0].selected = true;
      }
    });
  }

  function initPhoneFields(root) {
    var defaultCode = root.dataset.klaviyoPhoneCountryCode || '+1';
    root.querySelectorAll('[data-campus-banner-phone] [name="phone_country"]').forEach(function (select) {
      if (select.dataset.phoneCountryInit) return;
      select.dataset.phoneCountryInit = 'true';

      var preferred = select.dataset.defaultCountry || defaultCode;
      var matched = false;
      Array.prototype.forEach.call(select.options, function (option) {
        if (!matched && option.value === preferred) {
          option.selected = true;
          matched = true;
        } else {
          option.selected = false;
        }
      });
      if (!matched && select.options.length) {
        select.options[0].selected = true;
      }
    });
  }

  function setState(root, state) {
    root.setAttribute('data-state', state);
  }

  function syncFormAvailability(root) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;

    root.querySelectorAll('.campus-banner__show-desktop [data-campus-banner-form]').forEach(function (form) {
      setFormDisabled(form, isMobile);
    });

    root.querySelectorAll('.campus-banner__show-mobile [data-campus-banner-form]').forEach(function (form) {
      setFormDisabled(form, !isMobile);
    });
  }

  function setFormDisabled(form, disabled) {
    form.querySelectorAll('input, select, textarea, button').forEach(function (field) {
      field.disabled = disabled;
    });
  }

  function fieldValue(form, name) {
    var field = form.querySelector('[name="' + name + '"]');
    return field ? (field.value || '').trim() : '';
  }

  function setLoading(button, isLoading, defaultLabel) {
    button.disabled = isLoading;
    var label = button.querySelector('[data-button-label]');
    if (!label) return;
    label.textContent = isLoading ? 'Submitting…' : defaultLabel;
  }

  function buildPayload(form, root) {
    var email = fieldValue(form, 'email');
    var firstName = fieldValue(form, 'first_name');
    var phone = getPhoneE164(form, root);
    var selectedCountry = getSelectedPhoneCountry(form, root);
    // Submitting the form implies consent per the on-page disclaimer.
    var emailConsent = true;
    var smsConsent = !!phone;

    var subscriptions = {};
    if (emailConsent) {
      subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (smsConsent) {
      subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }

    var profileAttributes = {
      email: email
    };
    if (firstName) profileAttributes.first_name = firstName;
    if (phone) profileAttributes.phone_number = phone;
    // Klaviyo's displayed profile Location is separate from phone_number_region.
    // Without this, Klaviyo often falls back to IP / US store context (United States)
    // even when the customer picked another country in the phone selector.
    if (selectedCountry.countryName) {
      profileAttributes.location = { country: selectedCountry.countryName };
    }
    if (selectedCountry.countryIso) {
      profileAttributes.locale = 'en-' + selectedCountry.countryIso;
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              // "subscriptions" is a sibling of "attributes" here, not part
              // of the profile's own attributes — Klaviyo rejects it with
              // "'subscriptions' is not a valid field for the resource
              // 'profile'" if nested inside attributes instead.
              attributes: profileAttributes,
              subscriptions: subscriptions
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: root.dataset.klaviyoListId
            }
          }
        }
      }
    };

    var source = root.dataset.klaviyoSource;
    if (source) payload.data.attributes.custom_source = source;

    return payload;
  }

  function handleSubmit(root, form) {
    var button = form.querySelector('button[type="submit"]');
    var defaultLabel = button.querySelector('[data-button-label]')
      ? button.querySelector('[data-button-label]').textContent
      : button.textContent;
    var errorMessageText = form.querySelector('[data-error-message-text]');
    var publicKey = root.dataset.klaviyoPublicKey;
    var listId = root.dataset.klaviyoListId;

    return function (event) {
      event.preventDefault();

      if (!form.reportValidity()) return;

      if (!publicKey || !listId) {
        setState(root, 'error');
        if (errorMessageText) {
          errorMessageText.textContent = 'This form is not fully configured yet. Please try again later.';
        }
        return;
      }

      setState(root, 'idle');
      setLoading(button, true, defaultLabel);

      var payload = buildPayload(form, root);

      fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(publicKey), {
        method: 'POST',
        headers: {
          // Klaviyo's client API follows the JSON:API spec and expects this
          // exact media type — "application/json" is rejected outright.
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          if (response.ok || response.status === 202) {
            setState(root, 'success');
            var successMessage = form.querySelector('[data-success-message]');
            if (successMessage) successMessage.focus();
            form.reset();
            resetPhoneFields(form, root);
            return;
          }

          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (body) {
              // Klaviyo's error format: { errors: [{ detail, source, ... }] }
              var detail =
                body && body.errors && body.errors[0] && body.errors[0].detail;
              throw new Error(
                'Klaviyo subscription request failed with status ' +
                  response.status +
                  (detail ? ': ' + detail : '')
              );
            });
        })
        .catch(function (error) {
          // Full reason (e.g. invalid phone format, bad/duplicate list ID,
          // wrong public key) is logged here — check the browser console
          // and Network tab for the exact Klaviyo error detail.
          console.error('[campus-banner] Klaviyo subscription failed:', error);
          setState(root, 'error');
          var errorMessage = form.querySelector('[data-error-message]');
          if (errorMessage) errorMessage.focus();
        })
        .finally(function () {
          setLoading(button, false, defaultLabel);
        });
    };
  }

  function init(root) {
    initPhoneFields(root);
    syncFormAvailability(root);

    root.querySelectorAll('[data-campus-banner-form]').forEach(function (form) {
      if (form.dataset.campusBannerBound) return;
      form.dataset.campusBannerBound = 'true';
      form.addEventListener('submit', handleSubmit(root, form));
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-campus-banner]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-campus-banner]').forEach(syncFormAvailability);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
